import React from 'react'

import TextBody from './TextBody'
import Icon, { EIconId } from '../icons'

import { browserDetection, getConsentStatus } from './utils'
import {
  trackEvent,
  getPopUpBrowserEventForClosing,
  getPopUpBrowserEventForConfirmation,
  getPopUpBrowserEventForImpression,
} from '../../utils/gtm'

import MissingBrowserSupportImage from '../../assets/missing-browser-support-logo.png'

export type TModalState = 'invalid-ie' | 'invalid-safari' | 'closed' | null

const LegacyBrowserModal: React.FC<{ timeout: number }> = (props) => {
  const [hasCookieConsent, setCookieConsent] = React.useState(false)
  const [modalState, setModalState] = React.useState<TModalState>(null)

  const timeout = props.timeout || 60000
  const COOKIE_VALUE = 'mbs=true'

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const { invalidSafari, invalidIE } = browserDetection()
      // translate browser detection output to TModalState
      let state: TModalState = invalidSafari ? 'invalid-safari' : invalidIE ? 'invalid-ie' : 'closed'
      // nullify error if modal already acknowledged
      state = document.cookie.indexOf(COOKIE_VALUE) > -1 ? 'closed' : state
      setModalState(state)
    }, timeout)
    return () => clearTimeout(timer)
  }, [])

  React.useEffect(() => {
    // modal opened
    if (modalState === 'invalid-ie' || modalState === 'invalid-safari') {
      trackEvent(getPopUpBrowserEventForImpression(), hasCookieConsent)
    }
    // modal closed
    if (modalState === 'closed') {
      document.cookie = `${COOKIE_VALUE}; path=/; SameSite=Strict;`
    }
  }, [modalState])

  React.useEffect(() => {
    // check once for functional cookie consent status
    setCookieConsent(getConsentStatus('consentFunctional'))
  }, [])

  const handleOnClickClose = () => {
    trackEvent(getPopUpBrowserEventForClosing(), hasCookieConsent)
    setModalState('closed')
  }

  const handleOnClickButton = () => {
    trackEvent(getPopUpBrowserEventForConfirmation(), hasCookieConsent)
    setModalState('closed')
    location.assign('/dpl/c/service/browser-hilfe')
  }

  return modalState === 'invalid-ie' || modalState === 'invalid-safari' ? (
    <div
      className="flex fixed w-full h-full inset-x-0 inset-y-0 z-10 justify-center items-center"
      style={{ backgroundColor: 'rgba(0,0,0,.3)' }}
      data-testid="legacy-browser-modal"
    >
      <div className="relative px-16 pb-24 pt-20 mx-16 bg-white uif-shadow sm:px-48 sm:uif-max-w-560 md:px-48 md:pt-32">
        <Icon
          id={EIconId.CLOSE_IN_CIRCLE}
          className="w-32 absolute right-0 top-0 mr-8 mt-8 sm:mr-16 sm:mt-16"
          onClick={handleOnClickClose}
          data-testid="button-close"
        />
        <Icon id={EIconId.HSE_LOGO} className="w-60 mx-auto text-brand-orange md:uif-w-100" />
        <img
          className="mx-auto uif-w-100 mt-12 sm:uif-w-120 sm:mt-16 md:uif-w-260 md:mt-24"
          src={MissingBrowserSupportImage}
          alt=""
        />
        <TextBody browserType={modalState} />
        <button
          className="w-full px-48 py-16 mt-16 sm:mt-24 text-white font-bold outline-none bg-brand-orange cursor-pointer"
          onClick={handleOnClickButton}
          data-testid="button-more-info"
        >
          Mehr Informationen
        </button>
      </div>
    </div>
  ) : (
    <></>
  )
}

export default LegacyBrowserModal
